import ConfirmationDialog from "@components/ConfirmationDialog";
import CustomSnackBar from "@components/CustomSnackBar";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import {
  TABLE_BODY_CELL_FONT_SIZE,
  TABLE_HEADER_CELL_FONT_SIZE
} from "@constants/table";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from "@mui/material";
import { defaultLabelDisplayedRows } from "@utils/table";
import AdminPanelNavBar from "@views/ViewAdminPanel/components/AdminPanelNavBar";

import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import {
  ROUTING_ADMIN_EDIT_USER,
  ROUTING_ADMIN_NEW_USER
} from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState,
  setSuccessAlert
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useDeleteUser, useUsers } from "@services/api";
import { User } from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { columns } from "./UserTableTypes";

interface SnackBarProps {
  open: boolean;
  message: string;
  color: string;
}

const ViewUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });

  const { data: users, isFetching: isLoading } = useUsers();
  const mutationDeleteUser = useDeleteUser();
  const queryClient = useQueryClient();

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleNewUser = () => {
    navigate(`${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_NEW_USER}`);
  };

  const handleUpdateUser = (userId: string) => {
    navigate(
      `${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_EDIT_USER.replace(":id", userId.toString())}`
    );
  };

  const handleDeleteUser = (userId: string) => {
    if (userId.length === 0 || userToDelete === null) {
      return;
    }

    mutationDeleteUser.mutate(
      {
        user_id: userId
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["users"]
          });
          queryClient.refetchQueries({
            queryKey: ["users"]
          });
          setOpenConfirmationDialog(false);
          setSnackbarAlert(
            setSuccessAlert(
              t("admin.users.delete_user_success", {
                name: userToDelete.username
              })
            )
          );
          setUserToDelete(null);
        },
        onError: () => {
          setOpenConfirmationDialog(false);
          setSnackbarAlert(
            setErrorAlert(
              t("admin.users.delete_user_error", {
                name: userToDelete.username
              })
            )
          );
          setUserToDelete(null);
        }
      }
    );
  };

  return (
    <Box className="w-full p-4">
      <AdminPanelNavBar activeTab="users" />
      <Box className="justify-center w-full p-2 md:p-16">
        <Box className="m-4">
          <Tooltip title={t("common.newUser")}>
            <IconButton
              color="info"
              onClick={handleNewUser}
              disabled={isLoading}
              style={{ backgroundColor: "white" }}
            >
              <PersonAddIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Paper className="w-full m-4">
          <TableContainer key="users-table-container">
            <Table aria-label="users collapsible table" key="users-table">
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    key="users-collapse-column"
                    style={{ width: 5, borderRight: "1px solid #caccce" }}
                  ></StyledTableCell>
                  {columns.map((column, index) => (
                    <StyledTableCell
                      key={`users-${column.id}-column`}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        borderRight:
                          index === columns.length - 1
                            ? "none"
                            : "1px solid #caccce",
                        fontSize: TABLE_HEADER_CELL_FONT_SIZE
                      }}
                    >
                      {t(`users.${column.label}`)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRowsSkeleton
                    id="users-table-rows-skeleton"
                    numberOfRows={rowsPerPage}
                    columns={columns.length + 1}
                  />
                ) : (
                  (users || [])
                    .sort((a, b) => a.username.localeCompare(b.username))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <StyledTableRow
                        sx={{ "& > *": { borderBottom: "unset" } }}
                        key={`users-${row.uuid}-row`}
                      >
                        <TableCell component="th" scope="row" align="center">
                          <Box className="flex justify-center gap-2">
                            <Tooltip title={t("common.edit")}>
                              <IconButton
                                color="info"
                                onClick={() => handleUpdateUser(row.uuid)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setOpenConfirmationDialog(true);
                                  setUserToDelete(row);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          <Box className="flex justify-center gap-2">
                            <Box>{row.email}</Box>
                            <Box>
                              <Tooltip
                                title={t(
                                  row.email_verified
                                    ? "user.verified_email"
                                    : "user.not_verified_email"
                                )}
                              >
                                <VerifiedIcon
                                  color={
                                    row.email_verified ? "success" : "error"
                                  }
                                />
                              </Tooltip>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          <Box className="flex justify-center gap-2">
                            <Box>{row.username}</Box>
                            <Box>
                              {row.enabled && (
                                <Tooltip title={t("user.enabled_user")}>
                                  <PersonIcon color="success" />
                                </Tooltip>
                              )}
                              {!row.enabled && (
                                <Tooltip title={t("user.disabled_user")}>
                                  <PersonOffIcon color="error" />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.last_name}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                        >
                          {row.first_name}
                        </TableCell>
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage={t("table.rows_per_page")}
            labelDisplayedRows={defaultLabelDisplayedRows}
            component="div"
            count={(users || []).length ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          onConfirm={() => handleDeleteUser(userToDelete?.uuid || "")}
          title={t("admin.users.delete_user", {
            name: userToDelete?.username || ""
          })}
        />
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
      </Box>
    </Box>
  );
};

export default ViewUsers;
